// A port of SuiteEditor's EditingRules: hidden-syntax balancing, Backspace/Delete, emphasis toggles and restyles, line
// breaks and pasting. Every function works on a window of text `t` (a fragment's body, or the blocks of it an edit
// touches), with the body's bounds in window offsets, and returns window offsets. The Swift works on the whole text
// storage with the body's range; the rules never look past the lines they touch and the block they're in, so a
// window holding those gives the same answers.
import { findClusterBreak } from '@codemirror/state';
import {
  charIsLetter, charIsNumber, charIsWhitespace, escape, escapeLeading, fromPlainText, graphemes, hasPrefix, isCFWhitespaceOrNewline, isLinkAddress,
  LINK_SCHEMES, linkAddress as encodedLinkAddress, MAX_VERSE_INDENT, paragraphFence, trimCFWhitespace, trimCFWhitespaceAndNewlines, verseFence, type Alignment,
} from '@writing-suite/core';
import { alignment as alignmentOf, block, blockStyle } from './blocks.ts';
import {
  expandedOverMarkers, hiddenTester, markdownOf, pieces, plain, stateAt, transition, type InlineState, type Piece,
} from './markdown.ts';

/** A window of a fragment's text. `bodyFrom`/`bodyTo` are the body's bounds in window offsets; a bound outside the
 *  window (−1, or past its end) means the body goes on beyond it. */
export interface Window { t: string; bodyFrom: number; bodyTo: number }
/** An edit to make instead, and where the caret goes after it (`EditingRules.Fix`). An empty range and text: nothing
 *  to do (refused). */
export interface Fix { from: number; to: number; text: string; caret: number }

const BACKSLASH = 92, NL = 10;

/** A whole string as a window. */
export const whole = (t: string): Window => ({ t, bodyFrom: 0, bodyTo: t.length });

/** A lone `\n` becomes `\n\n` (a Markdown paragraph break); `\n\n` and hard breaks (`\` + `\n`) are kept
 *  (`EditingRules.normalizeNewlines`). */
export function normalizeNewlines(text: string): string {
  if (!/[\n\r]/.test(text)) return text;
  const s = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  let out = '';
  let i = 0;
  while (i < s.length) {
    if (s.charCodeAt(i) === NL) {
      let run = 0;
      while (i + run < s.length && s.charCodeAt(i + run) === NL) run++;
      const hardBreak = run === 1 && out.endsWith('\\');
      out += '\n'.repeat(hardBreak ? 1 : Math.max(2, run));
      i += run;
    } else out += s[i++];
  }
  return out;
}

/** Pasted plain text is prose: Markdown syntax characters escaped and lines made paragraphs (`EditingRules.prose`). */
export const proseFromPlainText = (text: string) => fromPlainText(text);

/** Backspace at the start of a paragraph: both newlines (`paragraphJoinBackward`). */
export function paragraphJoinBackward(w: Window, at: number): [number, number] | null {
  return at >= 2 && at - 2 >= w.bodyFrom && w.t.charCodeAt(at - 1) === NL && w.t.charCodeAt(at - 2) === NL ? [at - 2, at] : null;
}
/** Delete at the end of a paragraph: both newlines (`paragraphJoinForward`). */
export function paragraphJoinForward(w: Window, at: number): [number, number] | null {
  return at + 2 <= w.bodyTo && at + 2 <= w.t.length && w.t.charCodeAt(at) === NL && w.t.charCodeAt(at + 1) === NL ? [at, at + 2] : null;
}
/** `rangeOfComposedCharacterSequence(at: at - 1)` / `(at: at)`. */
export const clusterBefore = (t: string, at: number): [number, number] => [findClusterBreak(t, at, false), at];
export const clusterAfter = (t: string, at: number): [number, number] => [at, findClusterBreak(t, at, true)];

/** With markers hidden, an edit must never leave them unbalanced where the writer can't see them
 *  (`EditingRules.balancingHiddenSyntax`). Deleting only hidden syntax beside the caret deletes the visible character
 *  beyond it instead; a deletion or replacement takes in the hidden syntax at its edges and puts back exactly the
 *  markers that keep the text around it styled as it was, typed text styled like the first character it replaces.
 *  Null when the edit is fine as it is. */
export function balancingHiddenSyntax(w: Window, from: number, to: number, text: string, caret: number | null): Fix | null {
  if (to <= from) return null;
  const t = w.t;
  const hidden = hiddenTester(t, Math.max(0, w.bodyFrom), Math.min(t.length, w.bodyTo));
  // An escape's backslash is hidden too, but it isn't emphasis: it goes only with the character it escapes.
  const escape = (i: number) => hidden(i) && t.charCodeAt(i) === BACKSLASH;
  let range: [number, number] = [from, to];
  // Backspace or Delete on hidden syntax alone (a keystroke beside the caret, never an undo or a replacement): the
  // visible character beyond it, or nothing at all at the fragment's edge.
  if (text === '' && caret !== null && (caret === to || caret === from)) {
    let allHidden = true;
    for (let i = from; i < to; i++) if (!hidden(i)) { allHidden = false; break; }
    if (allHidden) {
      if (caret === to) {
        let j = from;
        while (j > w.bodyFrom && hidden(j - 1)) j--;
        if (j <= w.bodyFrom) return { from: caret, to: caret, text: '', caret };
        range = paragraphJoinBackward(w, j) ?? clusterBefore(t, j);
      } else {
        let j = to;
        while (j < w.bodyTo && hidden(j)) j++;
        if (j >= w.bodyTo) return { from: caret, to: caret, text: '', caret };
        range = paragraphJoinForward(w, j) ?? clusterAfter(t, j);
      }
    }
  }
  let [start, end] = range;
  // A selection ending on an escape's backslash keeps it (and so the character it escapes).
  if (end > start && escape(end - 1)) end--;
  // One starting just after an escape's backslash takes it with the character it escaped.
  if (start > w.bodyFrom && start < end && escape(start - 1)) start--;
  // The first visible character replaced decides how typed text looks.
  let first = start;
  while (first < end && hidden(first)) first++;
  const typedStyle = stateAt(t, first);
  // Take in the emphasis and span syntax at either edge, so runs of asterisks can't merge or split.
  while (start > w.bodyFrom && hidden(start - 1) && !escape(start - 1)) start--;
  while (end < w.bodyTo && hidden(end) && !escape(end)) end++;
  const before = stateAt(t, start), after = stateAt(t, end);
  const lead = text === '' ? '' : transition(before, typedStyle);
  const replacement = text === '' ? transition(before, after) : lead + text + transition(typedStyle, after);
  // Unchanged by all this: let the edit be made as it is.
  if (range[0] === from && range[1] === to && end >= to && t.slice(start, from) + text + t.slice(to, end) === replacement) return null;
  return { from: start, to: end, text: replacement, caret: start + lead.length + text.length };
}

/** Backspace or Delete: the range the keystroke removes (a paragraph join takes both newlines; a selection itself),
 *  or null at the body's edge. */
export function keyDeleteRange(w: Window, selFrom: number, selTo: number, backward: boolean): [number, number] | null {
  if (selTo > selFrom) return [selFrom, selTo];
  if (backward) return selFrom <= Math.max(0, w.bodyFrom) ? null : paragraphJoinBackward(w, selFrom) ?? clusterBefore(w.t, selFrom);
  return selFrom >= Math.min(w.t.length, w.bodyTo) ? null : paragraphJoinForward(w, selFrom) ?? clusterAfter(w.t, selFrom);
}

/** Typing after ⌘I/⌘B with nothing selected (`EditingRules.emphasised`): `text` at `at` with italic (an odd run of
 *  `marker`) or bold (two or more) turned the other way from the style there. The markers beside `at` are rebuilt with
 *  it; the caret stays inside the style, after the text. */
export function emphasised(w: Window, text: string, at: number, marker: string): Fix {
  const t = w.t;
  const style = stateAt(t, at);
  if (marker.length % 2 === 1) style.italic = !style.italic;
  if (marker.length >= 2) style.bold = !style.bold;
  const [ra, rb] = expandedOverMarkers(t, at, at);
  const built = markdownOf([{ kind: 'text', text, style }], stateAt(t, ra), stateAt(t, rb));
  return { from: ra, to: rb, text: built.text, caret: ra + built.spans[0][1] };
}

export interface Restyle { from: number; to: number; text: string; selection: [number, number] }

/** Every character the reader sees in `[from, to)` restyled by `apply`, whose `on` is false when all of them already
 *  satisfy `has` (`EditingRules.restyled`). The markers at its edges and inside are rebuilt from the styles, so a new
 *  marker never merges with a run beside it. Also returns the same words selected again. */
export function restyled(w: Window, from: number, to: number, has: (s: InlineState) => boolean,
  apply: (s: InlineState, on: boolean) => void): Restyle | null {
  const t = w.t;
  const [ra, rb] = expandedOverMarkers(t, from, to);
  const before = stateAt(t, ra);
  const ps = pieces(t, ra, rb, before);
  const styles = ps.flatMap((p) => (p.kind === 'text' ? [p.style] : []));
  if (styles.length === 0) return null;
  const on = !styles.every(has);
  const out: Piece[] = ps.map((p) => {
    if (p.kind !== 'text') return p;
    const style = { ...p.style };
    apply(style, on);
    return { ...p, style };
  });
  const built = markdownOf(out, before, stateAt(t, rb));
  const words = out.flatMap((p, i) => (p.kind === 'text' ? [built.spans[i]] : []));
  return { from: ra, to: rb, text: built.text, selection: [ra + words[0][0], ra + words[words.length - 1][1]] };
}

/** ⌘I / ⌘B over a selection (`EditingRules.toggledEmphasis`). */
export function toggledEmphasis(w: Window, from: number, to: number, marker: string): Restyle | null {
  const italic = marker.length % 2 === 1;
  return restyled(w, from, to, (s) => (italic ? s.italic : s.bold), (s, on) => { if (italic) s.italic = on; else s.bold = on; });
}

/** `pieces` in place of `[from, to)`, with the styles around it kept (`EditingRules.inserting`): the markers at its
 *  edges are taken in and rebuilt around the new text. The caret goes after it all. */
export function inserting(w: Window, ps: Piece[], from: number, to: number): Fix {
  const t = w.t;
  const [a, b] = expandedOverMarkers(t, from, to);
  const built = markdownOf(ps, stateAt(t, a), stateAt(t, b)).text;
  return { from: a, to: b, text: built, caret: a + built.length };
}

/** Prose typed or pasted as plain text (no markers of its own) over `[from, to)` (`EditingRules.typedPieces`): its
 *  first line takes the style of the first character it replaces (or of the place it's typed), its last line the
 *  style of what follows it. */
export function typedPieces(w: Window, markdown: string, from: number, to: number): Piece[] {
  const t = w.t;
  const [a, b] = expandedOverMarkers(t, from, to);
  const replaced = pieces(t, a, b, stateAt(t, a));
  let lead = stateAt(t, from);
  for (const p of replaced) if (p.kind === 'text') { lead = p.style; break; }
  const trail = stateAt(t, b);
  const ps = pieces(markdown, 0, markdown.length, plain());
  const lines = ps.filter((p) => p.kind === 'newline').length;
  let line = 0;
  return ps.map((p) => {
    if (p.kind === 'newline') { line++; return p; }
    if (p.kind === 'syntax') return p;
    return { ...p, style: line === 0 ? lead : line === lines ? trail : plain() };
  });
}

/** Return (`\n\n`) or Shift-Return (`\\\n`) in prose, when emphasis is open at the caret: closed before the break and
 *  opened again after it (`EditingRules.lineBreak`). Null when the break needs nothing more. */
export function lineBreak(w: Window, from: number, to: number, text: string): Fix | null {
  const fix = inserting(w, typedPieces(w, text, from, to), from, to);
  return fix.from === from && fix.to === to && fix.text === text ? null : fix;
}

/** A line of verse in a window's text (`EditingRules.VerseLine`): the block (the fence to its close), the content lines
 *  (each without its newline; the fence lines aren't among them), the line holding the caret, its leading tabs, the
 *  fence's alignment, and whether the line has no text but its tabs (a trailing hard-break backslash aside). */
export interface VerseLine {
  block: [number, number];
  lines: [number, number][];
  line: [number, number];
  tabs: number;
  alignment: Alignment | null;
  isEmpty: boolean;
}

const isVerseFence = (t: string) => paragraphFence(trimCFWhitespaceAndNewlines(t))?.kind.type === 'verse';

/** `EditingRules.verseLine(at:in:)`: the line of verse around `location` (window offsets), if the caret is on one (not on
 *  the fence lines). A verse fence may hold several stanzas parted by blank lines (the editor writes a fence per stanza,
 *  but a fence typed by hand, or an older one, holds them all), so the caret's stanza runs from the nearest blank line or
 *  fence above it to the next blank line or `:::` below it. A line holding only tabs is a line of it; an empty line is one
 *  only straight after the fence or a hard break, else it parts stanzas. `block` runs from the fence's opening line to
 *  the stanza's end. */
export function verseLine(w: Window, location: number): VerseLine | null {
  const off = Math.max(0, w.bodyFrom);
  const local = w.t.slice(off, Math.min(w.t.length, w.bodyTo));
  const lineAt = (i: number) => lineRangeIn(local, Math.min(Math.max(0, i), local.length));
  const content = (r: [number, number]) => local.slice(r[0], r[1]).replace(/\n$/, '');
  const trimmed = (r: [number, number]) => trimCFWhitespace(content(r));
  const endsInHardBreak = (r: [number, number]) => { const c = content(r); return c.endsWith('\\') && !c.endsWith('\\\\'); };
  const here = lineAt(location - off);
  if (paragraphFence(trimmed(here)) !== null || trimmed(here) === ':::') return null;
  if (local.lastIndexOf('verse', here[0] - 1) < 0 || here[0] === 0) return null;
  // Up to the fence: the stanza starts below the nearest blank line on the way.
  let opener = here;
  let start: number | null = null;
  let below = here;
  for (;;) {
    if (opener[0] <= 0) return null;
    opener = lineAt(opener[0] - 1);
    const t = trimmed(opener);
    if (paragraphFence(t)?.kind.type === 'verse') break;
    if (t === ':::' || paragraphFence(t) !== null || t === '::: epigraph') return null;
    if (content(opener).length === 0 && start === null) start = below[0];
    below = opener;
  }
  if (content(here).length === 0) {
    const above = lineAt(here[0] - 1);
    if (above[0] !== opener[0] && !endsInHardBreak(above)) return null;
  }
  const align = paragraphFence(trimmed(opener))?.alignment ?? null;
  // The stanza's lines down to its close (or an empty line, or the body's end).
  const lines: [number, number][] = [];
  let probe = lineAt(start ?? opener[1]);
  let end = opener[1];
  // (The caret's own empty line may be the body's end, past its last newline, in a fence not yet closed.)
  while (probe[0] < local.length || probe[0] === here[0]) {
    const t = trimmed(probe);
    if (t === ':::') { end = probe[0] + content(probe).length; break; }
    if (content(probe).length === 0 && probe[0] !== here[0]) break;
    lines.push([off + probe[0], off + probe[0] + content(probe).length]);
    end = probe[0] + content(probe).length;
    if (probe[1] >= local.length) {
      if (here[0] !== local.length || probe[0] === here[0]) break;
      probe = here;
      continue;
    }
    probe = lineAt(probe[1]);
  }
  const range: [number, number] = [off + opener[0], off + end];
  const line = lines.find(([a, b]) => location >= a && location <= b);
  if (!line) return null;
  let text = w.t.slice(line[0], line[1]);
  if (text.endsWith('\\') && !text.endsWith('\\\\')) text = text.slice(0, -1);
  let tabs = 0;
  while (tabs < text.length && text[tabs] === '\t') tabs++;
  return { block: range, lines, line, tabs, alignment: align, isEmpty: trimCFWhitespace(text).length === 0 };
}

/** `EditingRules.verseOpenerEnd`: `location` when it is the end of a verse fence's opening line with nothing after it yet
 *  (the body's end, an empty line or the `:::`), as when the fence was typed by hand; null otherwise. */
export function verseOpenerEnd(w: Window, location: number): number | null {
  const off = Math.max(0, w.bodyFrom);
  const local = w.t.slice(off, Math.min(w.t.length, w.bodyTo));
  const at = location - off;
  const line = lineRangeIn(local, Math.min(Math.max(0, at), local.length));
  const content = local.slice(line[0], line[1]).replace(/\n$/, '');
  if (paragraphFence(trimCFWhitespace(content))?.kind.type !== 'verse' || at !== line[0] + content.length) return null;
  if (!(line[1] < local.length && local.slice(line[0], line[1]).endsWith('\n'))) return location;
  const nextLine = lineRangeIn(local, line[1]);
  const next = local.slice(nextLine[0], nextLine[1]).trim();
  return next === '' || next === ':::' ? location : null;
}

/** `NSString.lineRange(for:)` at `i` in `t`: the line holding `i`, with its newline. */
function lineRangeIn(t: string, i: number): [number, number] {
  const start = i <= 0 ? 0 : t.lastIndexOf('\n', i - 1) + 1;
  const nl = t.indexOf('\n', i);
  return [start, nl < 0 ? t.length : nl + 1];
}

/** `EditingRules.versePaste`: plain text pasted into a verse block — each line a line of verse (a run of two to four
 *  leading spaces, or a tab, a step), a blank line a new stanza (its fence closed and another opened). Null when the
 *  caret isn't in verse or nothing would be pasted. */
export function versePaste(w: Window, text: string, from: number, to: number): Fix | null {
  const verse = verseLine(w, from);
  if (!verse) return null;
  const fence = verseFence(verse.alignment);
  let out = '';
  let pendingBreak = false;
  for (const raw of text.replaceAll('\r\n', '\n').replaceAll('\r', '\n').split('\n')) {
    let tabs = 0;
    while (tabs < raw.length && raw[tabs] === '\t') tabs++;
    let spaces = 0;
    while (tabs + spaces < raw.length && raw[tabs + spaces] === ' ') spaces++;
    const content = trimCFWhitespace(raw.slice(tabs + spaces));
    if (content === '') {
      if (out !== '') pendingBreak = true;
      continue;
    }
    if (out !== '') out += pendingBreak ? '\n:::\n\n' + fence + '\n' : '\\\n';
    pendingBreak = false;
    const steps = Math.min(MAX_VERSE_INDENT, tabs + (spaces < 2 ? 0 : Math.floor((spaces + 3) / 4)));
    out += escapeLeading('\t'.repeat(steps) + escape(content));
  }
  if (out === '') return null;
  return { from, to, text: out, caret: from + out.length };
}

/** Pasting over `[from, to)` in prose (`EditingRules.pasting`): the editor's own Markdown keeps its styles and lines
 *  exactly as copied; plain text becomes prose (escaped, a paragraph per line) in the style where it lands. */
export function pasting(w: Window, text: string, markdown: boolean, from: number, to: number): Fix {
  if (!markdown) {
    const verse = versePaste(w, text, from, to);
    if (verse) return verse;
  }
  // A web address pasted over words on one line links them, as in Pages and Notes.
  if (!markdown && to > from) {
    const address = pastedLinkAddress(text);
    const fix = address !== null && !w.t.slice(from, to).includes('\n') ? linked(w, from, to, address) : null;
    if (fix) return { from: fix.from, to: fix.to, text: fix.text, caret: fix.from + fix.text.length };
  }
  const source = markdown ? text : proseFromPlainText(text);
  if (!markdown && !source.includes('\n')) {
    return balancingHiddenSyntax(w, from, to, source, null) ?? { from, to, text: source, caret: from + source.length };
  }
  const ps = markdown ? pieces(source, 0, source.length, plain()) : typedPieces(w, source, from, to);
  return inserting(w, ps, from, to);
}

// MARK: Links

/** `trimmingCharacters(in: .whitespacesAndNewlines)`. */
function trimmedWhitespaceAndNewlines(s: string): string {
  let a = 0, b = s.length;
  while (a < b) { const cp = s.codePointAt(a)!; if (!isCFWhitespaceOrNewline(cp)) break; a += cp > 0xffff ? 2 : 1; }
  while (b > a) {
    const low = s.charCodeAt(b - 1);
    const n = low >= 0xdc00 && low <= 0xdfff && b - 2 >= a ? 2 : 1;
    if (!isCFWhitespaceOrNewline(s.codePointAt(b - n)!)) break;
    b -= n;
  }
  return s.slice(a, b);
}

const EMAIL = /^[^@\s/]+@[^@\s/]+\.[^@\s/]+$/u;

/** An address as the writer typed it, made a link's (`EditingRules.linkAddress(fromTyped:)`): a bare web address
 *  (`example.com/page`) gets `https://`, an email address `mailto:`, then it's encoded (`linkAddress`). Null when it
 *  can't be one. */
export function linkAddressFromTyped(typed: string): string | null {
  const text = trimmedWhitespaceAndNewlines(typed);
  if (text === '') return null;
  let address = text;
  const lower = text.toLowerCase();
  if (!LINK_SCHEMES.some((scheme) => hasPrefix(lower, scheme))) {
    const first = graphemes(text)[0];
    if (EMAIL.test(text)) address = 'mailto:' + text;
    else if (!text.includes(' ') && text.includes('.') && (charIsLetter(first) || charIsNumber(first))) address = 'https://' + text;
    else return null;
  }
  const encoded = encodedLinkAddress(address);
  return isLinkAddress(encoded) ? encoded : null;
}

/** Pasted text that's one web address and nothing else (`https://…`, `www.…`), for linking the selection
 *  (`EditingRules.pastedLinkAddress`). */
export function pastedLinkAddress(text: string): string | null {
  const t = trimmedWhitespaceAndNewlines(text);
  if (graphemes(t).some((c) => charIsWhitespace(c))) return null;
  const lower = t.toLowerCase();
  if (!(hasPrefix(lower, 'https://') || hasPrefix(lower, 'http://') || hasPrefix(lower, 'www.') || hasPrefix(lower, 'mailto:'))) return null;
  return linkAddressFromTyped(t);
}

/** `[from, to)` linked to `address` (null takes links off), every character the reader sees in it, the markup rebuilt
 *  around it as `restyled` does (`EditingRules.linked`). Also returns the words selected again. */
export function linked(w: Window, from: number, to: number, address: string | null): Restyle | null {
  return restyled(w, from, to, () => false, (s) => { s.link = address; });
}
